import { classnames } from '@/helpers';
import { DOMAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

type ListItemType = 'li' | 'div';

type LocationProps = {
  state: any;
  pathname: string;
};

export type ListItemProps = {
  children: ReactNode;
  leftSlot?: JSX.Element;
  rightSlot?: JSX.Element;
  underline?: boolean;
  leftPadding?: boolean;
  rightPadding?: boolean;
  className?: string;
  to?: LocationProps | string;
  targetBlank?: boolean;
  type?: ListItemType;
  verticalAlign?: 'center' | 'top';
} & Pick<DOMAttributes<HTMLButtonElement>, 'onClick'>;

const ListItemWrapper = ({ className, children, to, onClick, targetBlank = false, ...props }) => {
  const isLink = to;
  if (isLink) {
    /**
     * Check if to is an object with a pathname property or jusa string pathname
     */
    const pathname = typeof to === 'object' ? to.pathname : to.split('?')[0];
    const search = typeof to === 'object' ? to.search : to.split('?')[1];

    return to.state || to.startsWith('/') ? (
      <Link className={className} to={{ pathname, search, state: to?.state }} onClick={onClick} {...props}>
        {children}
      </Link>
    ) : (
      <a className={className} href={to} {...(targetBlank && { target: 'blank' })} onClick={onClick} {...props}>
        {children}
      </a>
    );
  }

  if (onClick) {
    return (
      <button className={className} type="button" onClick={onClick} {...props}>
        {children}
      </button>
    );
  }

  return (
    <div className={className} {...props}>
      {children}
    </div>
  );
};

const ListItem = ({
  to,
  onClick,
  children,
  leftSlot,
  rightSlot,
  leftPadding = true,
  rightPadding = true,
  underline,
  className = '',
  verticalAlign = 'center',
  type = 'li',
  targetBlank,
  ...props
}: ListItemProps) => {
  return (
    <>
      {type === 'li' && (
        <li className="relative w-full list-none">
          <ListItemWrapper
            to={to}
            targetBlank={targetBlank}
            onClick={onClick}
            className={classnames(
              'focus:outline-black-900 block w-full text-current outline-none',
              leftPadding && 'pl-lg',
            )}
            {...props}>
            <div
              className={twMerge(
                classnames(
                  'border-black-100 py-md gap-md flex w-full text-start',
                  underline && 'border-b',
                  verticalAlign === 'top' ? 'items-start' : 'items-center',
                  rightPadding && 'pr-lg',
                ),
                className,
              )}>
              {leftSlot && <div className="min-w-[24px] flex-shrink-0">{leftSlot}</div>}
              <div className="flex-1 overflow-hidden">{children}</div>
              {rightSlot && <div className="flex-shrink-0">{rightSlot}</div>}
            </div>
          </ListItemWrapper>
        </li>
      )}
      {type === 'div' && (
        <div className="relative w-full list-none">
          <ListItemWrapper
            to={to}
            targetBlank={targetBlank}
            onClick={onClick}
            className={classnames('focus:outline-black-900 block w-full text-current', leftPadding && 'pl-lg')}>
            <span
              className={twMerge(
                classnames(
                  'border-black-100 py-md gap-md flex w-full text-start',
                  underline && 'border-b',
                  verticalAlign === 'top' ? 'items-start' : 'items-center',
                  rightPadding && 'pr-lg',
                ),
                className,
              )}
              {...props}>
              {leftSlot && <div className="min-w-[24px] flex-shrink-0">{leftSlot}</div>}
              <div className="flex-1 overflow-hidden">{children}</div>
              {rightSlot && <div className="flex-shrink-0">{rightSlot}</div>}
            </span>
          </ListItemWrapper>
        </div>
      )}
    </>
  );
};

/**
 * New ListItem component from SAS design system
 */

type ListItemBaseProps = {
  children: ReactNode;
  rightSlot?: JSX.Element;
  onClick?: () => void;
  className?: string;
  to?: string | { pathname: string; search?: string; state?: any };
  targetBlank?: boolean;
  type?: ListItemType;
  showRightChevron?: boolean;
};

const Base = ({
  to,
  onClick,
  children,
  rightSlot,
  className = '',
  targetBlank,
  type = 'li',
  showRightChevron = true,
  ...props
}: ListItemBaseProps) => {
  const content = (
    <ListItemWrapper
      to={to}
      targetBlank={targetBlank}
      onClick={onClick}
      className={classnames(
        'bg-bg_secondary_neutral focus-within:ring-border_focus hover:bg-bg_secondary_hover active:bg-bg_secondary_pressed block w-full rounded-sm text-current outline-none focus-within:ring-[3px]',
      )}
      {...props}>
      <div
        className={twMerge(
          classnames('p-sm pl-xs gap-md relative flex w-full list-none items-center rounded-sm text-start'),
          className,
        )}>
        <div className="flex-1 overflow-hidden">{children}</div>

        <div className="flex flex-shrink-0 items-center">
          {rightSlot}
          {showRightChevron && (
            <svg width="5" height="11" viewBox="0 0 5 11" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.167823 0.626301C0.374215 0.442842 0.690251 0.461432 0.87371 0.667823L4.87371 5.16782C5.0421 5.35727 5.0421 5.64274 4.87371 5.83219L0.87371 10.3322C0.690251 10.5386 0.374215 10.5572 0.167823 10.3737C-0.038568 10.1903 -0.0571583 9.87422 0.126301 9.66782L3.83103 5.50001L0.126301 1.33219C-0.0571583 1.1258 -0.038568 0.80976 0.167823 0.626301Z"
                fill="var(--color-fg-primary)"
              />
            </svg>
          )}
        </div>
      </div>
    </ListItemWrapper>
  );

  if (type === 'li') {
    return <li className="list-none">{content}</li>;
  }

  return content;
};

ListItem.Base = Base;

export default ListItem;
